/**
 * The dispatch console's request contract — M5, ADR-0040.
 *
 * Only the request side lives here, matching `shipments.ts`'s split: a response is a
 * server-computed `DispatchSolution` projected through `opsUser`, and there is nothing a
 * client submits that shapes it. What a client *does* submit — how many candidates to plan
 * over, and later, which suggestion to approve — needs the same runtime validation on both
 * sides of the wire, which is the whole reason this package exists.
 */
import { z } from 'zod';
import { renderedText } from './rendered-text.js';
/** How many candidates to plan over in one run. Mirrors `opsBoardSchema`'s bounds. */
export const dispatchPlanRequestSchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const dispatchPlanRequestRendered = renderedText(dispatchPlanRequestSchema, {});
/**
 * One stop on the route being approved — the wire shape of a kernel `DispatchStop`.
 *
 * Round-tripped **verbatim**: the console sends back exactly what `/plan` handed it, never
 * a value it computed itself (ADR-0040 D5). Approving is choosing which suggestion to act
 * on, not re-describing one — a client that could edit a stop's pin or ETA on the way back
 * would let a tampered value reach the log looking approved.
 */
export const approveDispatchStopSchema = z.object({
    kind: z.enum(['pickup', 'delivery']),
    orderId: z.string().trim().min(1).max(64),
    shipmentId: z.string().trim().min(1).max(64),
    pin: z.object({ lat: z.number(), lng: z.number() }),
    etaOffsetSeconds: z.number().int().min(0),
});
/**
 * Approve one route from a suggestion — `POST /ops/dispatch/approve` takes exactly this.
 *
 * A tampered `courierId` is not caught here; it is caught by the **real** guards
 * `assignment.accept` runs at apply time, which is the point (D6) — this schema only
 * proves the shape survived the wire, never that the content is still true.
 */
export const approveRouteSchema = z.object({
    runRef: z.string().trim().min(1).max(64),
    courierId: z.string().trim().min(1).max(64),
    stops: z.array(approveDispatchStopSchema).min(2),
    routingVersion: z.string().trim().min(1).max(64),
    dispatchVersion: z.string().trim().min(1).max(64),
});
const ROUTE_PLAN_ONLY = 'stored only in the shipment.route_plan.set event payload; no view renders it';
/**
 * `runRef` and every stop's `shipmentId` come back in the approval's own response as sent, and
 * `courierId` becomes the Legs' Courier once `assignment.accept` passes, so all three are references.
 */
export const approveRouteRendered = renderedText(approveRouteSchema, {
    refs: ['runRef', 'courierId', 'stops.*.shipmentId'],
    exempt: {
        'stops.*.orderId': ROUTE_PLAN_ONLY,
        routingVersion: ROUTE_PLAN_ONLY,
        dispatchVersion: ROUTE_PLAN_ONLY,
    },
});
//# sourceMappingURL=dispatch.js.map